import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, Search, UserPlus, ArrowRight, Clock, ChevronRight, Filter,
} from "lucide-react";

const StaffPatients = () => {
  const { token } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      const res = await fetch(`${API}/admin/users?limit=5000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPatients((data.users || []).filter(u => !["admin", "dietitian", "corporate_admin", "partner_admin"].includes(u.role)));
      }
    } catch (err) {
      console.error("Failed to fetch patients:", err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = patients.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q)
    );
  });

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return "No activity";
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    return `${Math.floor(days / 7)} weeks ago`;
  };

  return (
    <div className="space-y-6" data-testid="staff-patients">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Patients</h1>
          <p className="mt-1 text-base text-stone-500">
            {filtered.length} {filtered.length === 1 ? "participant" : "participants"} in the system
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-11 rounded-xl border-stone-300 text-sm"
          data-testid="patient-search"
        />
      </div>

      {/* Patient list */}
      <Card className="rounded-xl border-stone-200/60 overflow-hidden">
        {/* Table header */}
        <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-stone-50 text-xs font-semibold text-stone-500 uppercase tracking-wider border-b border-stone-200/60">
          <div className="col-span-5">Patient</div>
          <div className="col-span-3 hidden md:block">Last Activity</div>
          <div className="col-span-4 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-stone-400">Loading participants...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-8 h-8 text-stone-300 mx-auto mb-3" />
            <p className="text-sm text-stone-500">
              {search ? "No participants match your search." : "No participants yet."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {filtered.map((patient) => (
              <div key={patient.user_id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-5 py-3.5 items-center hover:bg-stone-50/50 transition-colors">
                {/* Name */}
                <div className="sm:col-span-5 flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[#FF7A00]/10 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-[#FF7A00]">{(patient.name || "?")[0].toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{patient.name || "Unknown"}</p>
                    <p className="text-xs text-stone-400 truncate">{patient.email}</p>
                  </div>
                </div>

                {/* Last activity */}
                <div className="sm:col-span-3 hidden md:flex items-center gap-1.5 text-xs text-stone-400">
                  <Clock className="w-3.5 h-3.5" />
                  {getTimeAgo(patient.last_meal_date || patient.created_at)}
                </div>

                {/* Actions */}
                <div className="sm:col-span-4 flex justify-end">
                  <Link to={`/admin/patients/${patient.user_id}`}>
                    <Button variant="ghost" size="sm" className="text-xs text-stone-500 hover:text-[#FF7A00] rounded-lg" data-testid={`view-patient-${patient.user_id}`}>
                      View
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default StaffPatients;
